// LO QUE EL CLUB DEBE · mig 341
//
// Cuando el club cancela una sesión y el niño iba por suscripción, se le da una
// sesión gratis que caduca al mes (`user_credits` con `origen_sesion_id`). Esto
// es lo que el panel enseña de esa promesa.
//
// ⚠️ LA DEUDA SE CALCULA, NO SE GUARDA, y es una decisión con historia: la mig
// 320 existió porque había DOS contadores de lo mismo y derivaron. Así que aquí
// no hay ninguna columna «sesiones_debidas» que alguien tenga que acordarse de
// bajar: se cuentan las compensaciones sin usar y sin caducar, y el día que un
// niño gasta la suya la cuenta baja sola porque la fila cambia.
//
// La frontera entre «viva» y «caducada» la pone la MISMA condición que usa
// `enroll_user` para elegir un crédito —`sessions_remaining > 0` y
// `expiry_date > now()`—, y no una parecida. Si aquí se escribiera `>=` o se
// olvidara el `remaining`, el panel diría que el club debe algo que el niño ya
// no puede gastar.

/** Una compensación tal como sale de `user_credits`, con el alumno resuelto. */
export interface FilaCompensacion {
  id: string;
  user_id: string;
  sessions_remaining: number;
  expiry_date: string | null;
  gift_reason: string | null;
  credit_type: string;
  alumno: string | null;
}

export interface CompensacionPintada extends FilaCompensacion {
  /** `true` si todavía se puede gastar. */
  viva: boolean;
  /** Días que quedan, negativo si ya caducó. `null` sin fecha. */
  diasRestantes: number | null;
}

const DIA = 24 * 60 * 60 * 1000;

/**
 * Decide si una compensación sigue siendo deuda.
 *
 * ⚠️ `expiry_date` NULL cuenta como NO viva, al contrario de lo que hace
 * `enroll_user` (que trata NULL como «no caduca nunca»). Y es a propósito: la
 * mig 341 pone un CHECK que impide que una compensación nazca sin fecha, así
 * que un NULL aquí no es «eterna», es una fila que no debería existir — y
 * contarla como deuda escondería el defecto en una cifra que cuadra.
 */
export function estaViva(f: FilaCompensacion, ahora: Date): boolean {
  if (f.sessions_remaining <= 0) return false;
  if (!f.expiry_date) return false;
  return new Date(f.expiry_date).getTime() > ahora.getTime();
}

export function diasQueQuedan(f: FilaCompensacion, ahora: Date): number | null {
  if (!f.expiry_date) return null;
  return Math.ceil((new Date(f.expiry_date).getTime() - ahora.getTime()) / DIA);
}

/** Las filas listas para pintar, las vivas primero y por caducidad más cercana. */
export function compensacionesPintadas(
  filas: readonly FilaCompensacion[],
  ahora: Date
): CompensacionPintada[] {
  return filas
    .map((f) => ({ ...f, viva: estaViva(f, ahora), diasRestantes: diasQueQuedan(f, ahora) }))
    .sort((a, b) => {
      if (a.viva !== b.viva) return a.viva ? -1 : 1;
      const da = a.expiry_date ?? '';
      const db = b.expiry_date ?? '';
      return da.localeCompare(db);
    });
}

export interface ResumenDeuda {
  /** Sesiones que el club debe y que todavía se pueden gastar. */
  sesiones: number;
  /** A cuántos alumnos distintos. */
  alumnos: number;
  /** Vivas que caducan en 7 días o menos: son las que hay que avisar. */
  caducanPronto: number;
  /** Emitidas que caducaron sin gastarse. No son deuda, son promesa perdida. */
  caducadasSinUsar: number;
}

export function resumenDeLoQueSeDebe(
  filas: readonly FilaCompensacion[],
  ahora: Date
): ResumenDeuda {
  const vivas = filas.filter((f) => estaViva(f, ahora));
  const alumnos = new Set(vivas.map((f) => f.user_id));
  return {
    sesiones: vivas.reduce((s, f) => s + f.sessions_remaining, 0),
    alumnos: alumnos.size,
    caducanPronto: vivas.filter((f) => (diasQueQuedan(f, ahora) ?? 99) <= 7).length,
    // Sin usar Y caducada. Una gastada no cuenta: esa se cumplió.
    caducadasSinUsar: filas.filter((f) => f.sessions_remaining > 0 && !estaViva(f, ahora)).length,
  };
}

/** El texto de la tarjeta. Dice el cero con palabras, no con un «0» suelto. */
export function textoDeLaDeuda(r: ResumenDeuda): string {
  if (r.sesiones === 0) {
    return r.caducadasSinUsar > 0
      ? 'El club no debe ninguna sesión ahora mismo.'
      : 'El club no debe ninguna sesión.';
  }
  const s = r.sesiones === 1 ? '1 sesión' : `${r.sesiones} sesiones`;
  const a = r.alumnos === 1 ? '1 alumno' : `${r.alumnos} alumnos`;
  return `El club debe ${s} a ${a}.`;
}
